import { JSX } from 'react';
import { useStore } from '@betgames/bg-state-manager';
import { useLocation, useSearchParams } from 'react-router-dom';
import classNames from 'classnames';
import { HowToPlayModal } from '@bg-components/Modals/HowToPlayModal';
import { ResultsModal } from '@bg-components/Modals/ResultsModal';
import { BetHistoryModal } from '@bg-components/Modals/BetHistoryModal';
import {
    HistoryIcon,
    QuestionIcon,
    ListIcon,
    SettingsIcon,
    FullScreenExitIcon,
    FullScreenIcon,
} from '@bg-components/Icon';
import { TopSettingsModal } from '@bg-components/Modals/TopSettingsModal';
import { Tooltip } from '@bg-components/Tooltip';
import { NavigationItem } from '@bg-components/NavigationItem';
import {
    t,
    authStore,
    IModalProps,
    useActiveModalName,
    sessionStore,
    iframeIntegration,
    TooltipName,
    tooltipQueueStore,
    tooltipQueueSelectors,
    playerSettingsApi,
    useMatchedGameId,
    CustomCssClassNames,
    useModalsManager,
    ModalName,
    ClassicGameDefinition,
    useFullscreen,
    fullScreenStore,
    routeUrlGenerator,
    GameId,
    partnerSettingsStore,
} from '@bg-shared';
import { HeroProfileNavigationItem } from '@iframe-shell/components/TopNavigation/HeroProfileNavigationItem';
import { MediaQueryTitle } from '../MediaQueryTitle';
import { HomeButton } from './HomeButton';
import { GamesSettingsDefinition } from './GamesSettingsDefinition';
import { LobbyButton } from './LobbyButton';
import classes from './TopNavigation.module.scss';

export const TopNavigation = (): JSX.Element => {
    const [, setSearchParams] = useSearchParams();
    const location = useLocation();
    const modalManager = useModalsManager();
    const authorized = useStore(authStore);
    const currentPageIsLobby = location.pathname.includes(routeUrlGenerator.routes.LOBBY);
    const activeModalName = useActiveModalName(modalManager);
    const gameId = useMatchedGameId();
    const { isFullscreen, isFSSupported } = useFullscreen();
    const settingsConfig = GamesSettingsDefinition.getSettings(gameId);
    const settingsTooltip = useStore(
        tooltipQueueStore,
        tooltipQueueSelectors.getTooltip(TooltipName.CASINO_WELCOME),
    );
    const isHZGame = ClassicGameDefinition.isHZGame(gameId);
    const isPLSGame = ClassicGameDefinition.isPLSGame(gameId);
    const showGameRelatedItems =
        !ClassicGameDefinition.isThirdPartyGame(gameId) && gameId !== GameId.RGS_ROULETTE_EURO;
    const showResults = !ClassicGameDefinition.isGameWithoutResults(gameId);
    const showFSButton = (isHZGame || isPLSGame) && isFSSupported;
    const showHTP = !(ClassicGameDefinition.isGameWithoutHowToPlay(gameId) || currentPageIsLobby);

    const onNavigationClick = <T extends IModalProps>(
        name: ModalName,
        Modal: (props: T) => JSX.Element,
        props?: T,
    ) => {
        modalManager.closeAll();
        iframeIntegration.scrollToRelative();
        modalManager.open(name, Modal, {
            onClose: () => {
                setSearchParams({}, { replace: true });
            },
            ...props,
        });
    };

    const handleSettingsTooltipClose = () => {
        playerSettingsApi.update((store) => {
            store.casinoFallbackTooltipSeen = true;
        });
        tooltipQueueStore.removeFirst();
    };

    const getIsActive = (name: ModalName): boolean => activeModalName === name;

    return (
        <nav
            className={classNames(classes.navigation, CustomCssClassNames.TOP_NAVIGATION)}
            data-qa="area-top-navigation"
        >
            {!!sessionStore.homeUrl && <HomeButton homeUrl={sessionStore.homeUrl} />}
            <div className={classes.lobby}>{!sessionStore.singleGame && <LobbyButton />}</div>
            <div className={classes.items}>
                {showHTP && (
                    <NavigationItem
                        onClick={(openerElementRef) => {
                            onNavigationClick(ModalName.HOW_TO_PLAY, HowToPlayModal, {
                                openerElementRef,
                            });
                        }}
                        className={classes.navigationItem}
                        dataQa="link-top-navigation-how-to-play"
                        active={getIsActive(ModalName.HOW_TO_PLAY)}
                    >
                        <QuestionIcon className={classes.icon} />
                        <MediaQueryTitle translationKey="how_to_play" className={classes.title} />
                    </NavigationItem>
                )}
                {showResults && (
                    <NavigationItem
                        onClick={(openerElementRef) => {
                            onNavigationClick(ModalName.RESULTS, ResultsModal, {
                                openerElementRef,
                            });
                        }}
                        className={classes.navigationItem}
                        dataQa="link-top-navigation-results"
                        active={getIsActive(ModalName.RESULTS)}
                    >
                        <ListIcon className={classes.icon} />
                        <MediaQueryTitle translationKey="results" className={classes.title} />
                    </NavigationItem>
                )}
                {authorized && (
                    <NavigationItem
                        onClick={(openerElementRef) => {
                            onNavigationClick(ModalName.BET_HISTORY, BetHistoryModal, {
                                gameId,
                                openerElementRef,
                            });
                        }}
                        className={classes.navigationItem}
                        dataQa="link-top-navigation-history"
                        active={getIsActive(ModalName.BET_HISTORY)}
                    >
                        <HistoryIcon className={classes.icon} />
                        <MediaQueryTitle translationKey="bet_history" className={classes.title} />
                    </NavigationItem>
                )}
                {showFSButton && (
                    <NavigationItem
                        onClick={() => {
                            fullScreenStore.toggle();
                        }}
                        className={classes.navigationItem}
                        dataQa="link-top-navigation-fullscreen"
                        active={isFullscreen}
                        id="top-navigation-fullscreen"
                    >
                        {isFullscreen ? (
                            <FullScreenExitIcon className={classes.icon} />
                        ) : (
                            <FullScreenIcon className={classes.icon} />
                        )}
                    </NavigationItem>
                )}
                {!!settingsConfig.length && showGameRelatedItems && (
                    <>
                        <NavigationItem
                            onClick={(openerElementRef) => {
                                onNavigationClick(ModalName.TOP_SETTINGS, TopSettingsModal, {
                                    openerElementRef,
                                    settings: settingsConfig,
                                    gameId,
                                });
                                handleSettingsTooltipClose();
                            }}
                            className={classes.navigationItem}
                            dataQa="link-top-navigation-settings"
                            active={getIsActive(ModalName.TOP_SETTINGS)}
                            id="top-navigation-settings"
                        >
                            <SettingsIcon className={classes.icon} />
                            <MediaQueryTitle translationKey="settings" className={classes.title} />
                        </NavigationItem>
                        <Tooltip
                            anchorSelect="#top-navigation-settings"
                            place="bottom-end"
                            className={classes.tooltip}
                            isOpen={settingsTooltip.show}
                            duration={settingsTooltip.duration}
                            onTimeout={handleSettingsTooltipClose}
                        >
                            {t.string('casino_welcome_tooltip')}
                        </Tooltip>
                    </>
                )}

                {partnerSettingsStore.partnerSettings.isGamificationEnabled && (
                    <HeroProfileNavigationItem
                        className={classes.gamificationNavigationItem}
                        active={getIsActive(ModalName.GAMIFICATION)}
                        dataQa="link-top-navigation-hero"
                        onClick={<T extends IModalProps>(
                            name: ModalName,
                            Modal: (props: T) => JSX.Element,
                            props: T,
                        ) => {
                            onNavigationClick(name, Modal, props);
                        }}
                    />
                )}
            </div>
        </nav>
    );
};
